import { Link } from "react-router-dom";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import Logo, { LOGO_URL } from "@/components/Logo";
import {
  KenteBorder, AdinkraDots, KenteStripes, PatternDivider, AdinkraSymbol,
} from "@/components/EthnicPatterns";
import { useLang, LanguageToggle } from "@/i18n";
import {
  Mic, FileText, Sparkles, Search, ListChecks, Download,
  Calendar, ChevronRight, Check, ArrowRight,
  Building2, Landmark, HeartHandshake, Radio, Briefcase,
  Linkedin, Facebook, Twitter, Play,
  Bell, Home, Users, Settings as SettingsIcon, BarChart3, Menu, X,
} from "lucide-react";

/* ---------- Section: Header ---------- */
export function Header() {
  const { t } = useLang();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuLinks = [
    { to: "/features", label: t.nav.features, testId: "mobile-nav-features" },
    { to: "/pricing", label: t.nav.pricing, testId: "mobile-nav-pricing" },
    { to: "/about", label: t.nav.about, testId: "mobile-nav-about" },
    { to: "/contact", label: t.nav.contact, testId: "mobile-nav-contact" },
  ];
  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#FAF9F6]/85 border-b border-af">
      <div className="max-w-7xl mx-auto px-3 sm:px-6 py-3 flex items-center justify-between gap-2 sm:gap-4">
        <Link to="/" data-testid="landing-logo" className="shrink-0" onClick={() => setMenuOpen(false)}>
          <span className="hidden sm:block"><Logo size={88} withWordmark tagline /></span>
          <span className="sm:hidden"><Logo size={44} /></span>
        </Link>
        <nav className="hidden lg:flex items-center gap-9 text-sm text-af-secondary">
          <Link to="/features" data-testid="nav-features-link" className="hover:text-terracotta transition-colors">{t.nav.features}</Link>
          <Link to="/pricing" data-testid="nav-pricing-link" className="hover:text-terracotta transition-colors">{t.nav.pricing}</Link>
          <Link to="/about" data-testid="nav-about-link" className="hover:text-terracotta transition-colors">{t.nav.about}</Link>
          <Link to="/contact" data-testid="nav-contact-link" className="hover:text-terracotta transition-colors">{t.nav.contact}</Link>
        </nav>
        <div className="flex items-center gap-1.5 sm:gap-2.5">
          <LanguageToggle className="hidden sm:inline-flex" />
          <Link to="/auth/login" data-testid="header-login-btn">
            <Button variant="outline" className="border-terracotta text-terracotta hover:bg-[#F5EFD8] rounded-md font-semibold px-2.5 sm:px-4 text-xs sm:text-sm">
              {t.nav.login}
            </Button>
          </Link>
          <Link to="/auth/signup" data-testid="header-signup-btn">
            <Button className="bg-terracotta hover:bg-[#9C7124] text-white rounded-md font-semibold px-2.5 sm:px-4 text-xs sm:text-sm">
              {t.nav.signup}
            </Button>
          </Link>
          <button
            onClick={() => setMenuOpen((o) => !o)}
            data-testid="mobile-menu-btn"
            aria-label="Menu"
            aria-expanded={menuOpen}
            aria-controls="mobile-menu"
            className="lg:hidden h-9 w-9 flex items-center justify-center rounded-md border border-af text-af-secondary hover:text-terracotta transition-colors"
          >
            {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>
      {menuOpen && (
        <nav id="mobile-menu" className="lg:hidden border-t border-af bg-af-card px-4 py-3" data-testid="mobile-menu">
          {menuLinks.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={l.testId}
              onClick={() => setMenuOpen(false)}
              className="block py-3 text-sm font-semibold text-af-secondary hover:text-terracotta border-b border-af last:border-0"
            >
              {l.label}
            </Link>
          ))}
          <div className="pt-3">
            <LanguageToggle />
          </div>
        </nav>
      )}
      <KenteBorder className="h-1.5 w-full" />
    </header>
  );
}

/* ---------- Hero with Dashboard Preview ---------- */
function DashboardPreview() {
  const { t } = useLang();
  const p = t.preview;
  const meetingIcons = [Calendar, Calendar, Calendar, Calendar];
  const badgeIcons = [Check, ListChecks, FileText];

  return (
    <div className="relative w-full bg-af-card border border-af rounded-md shadow-[0_8px_32px_-12px_rgba(184,134,45,0.18)] overflow-hidden">
      {/* Window chrome */}
      <div className="px-5 py-3 bg-af-subtle border-b border-af flex items-center gap-2">
        <div className="flex gap-1.5">
          <span className="h-2.5 w-2.5 rounded-full bg-[#FF5F57]" />
          <span className="h-2.5 w-2.5 rounded-full bg-[#FEBC2E]" />
          <span className="h-2.5 w-2.5 rounded-full bg-[#28C840]" />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-[180px_1fr] min-h-0 sm:min-h-[420px]">
        {/* Sidebar */}
        <div className="hidden sm:flex bg-af-bg border-r border-af p-4 flex-col gap-1">
          <div className="flex items-center gap-2 mb-5 px-2">
            <Logo size={24} />
            <span className="font-display font-extrabold text-sm">AfriMeet</span>
          </div>
          {[
            { i: Home, l: p.sidebar.home, a: true },
            { i: Calendar, l: p.sidebar.meetings },
            { i: BarChart3, l: p.sidebar.team },
            { i: SettingsIcon, l: p.sidebar.settings },
          ].map((it) => (
            <div key={it.l}
              className={`flex items-center gap-2 px-2.5 py-1.5 rounded-md text-[11px] ${
                it.a ? "bg-[#F5EFD8] text-terracotta font-semibold" : "text-af-secondary"
              }`}>
              <it.i className="h-3.5 w-3.5" /> {it.l}
            </div>
          ))}
        </div>

        {/* Main content */}
        <div className="p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="font-display font-bold text-sm">{p.title}</div>
            <div className="flex items-center gap-2.5">
              <Bell className="h-3.5 w-3.5 text-af-muted" />
              <div className="flex items-center gap-1.5">
                <div className="h-5 w-5 rounded-full bg-[#D4A04E]" />
                <span className="text-[10px] font-medium">{p.user}</span>
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
            {p.stats.map((s) => (
              <div key={s.l} className="bg-white border border-af rounded-md p-2.5">
                <div className="font-display font-extrabold text-base text-af leading-tight">{s.v}</div>
                <div className="text-[9px] text-af-muted mt-0.5">{s.l}</div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-2">
            {/* Recent meetings */}
            <div className="bg-white border border-af rounded-md p-3">
              <div className="text-[10px] font-bold mb-2">{p.meetingsTitle}</div>
              <div className="space-y-1.5">
                {p.meetings.map((m, i) => {
                  const Icon = meetingIcons[i] || Calendar;
                  return (
                    <div key={m.t} className="flex items-center gap-1.5 py-1 border-b last:border-0 border-af">
                      <div className="h-5 w-5 rounded bg-[#F5EFD8] flex items-center justify-center">
                        <Icon className="h-2.5 w-2.5 text-terracotta" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[9px] font-semibold truncate">{m.t}</div>
                        <div className="text-[8px] text-af-muted">{m.d}</div>
                      </div>
                      <ChevronRight className="h-2.5 w-2.5 text-af-muted" />
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Summary */}
            <div className="bg-white border border-af rounded-md p-3">
              <div className="text-[10px] font-bold mb-2">{p.summaryTitle}</div>
              <ul className="space-y-1.5">
                {p.summaryBullets.map((b) => (
                  <li key={b} className="text-[9px] text-af-secondary leading-snug flex gap-1">
                    <span className="text-terracotta">•</span> {b}
                  </li>
                ))}
              </ul>
              <button className="mt-3 w-full bg-terracotta text-white rounded text-[9px] py-1 font-semibold">
                {p.viewSummary}
              </button>
            </div>

            {/* Badges */}
            <div className="flex flex-row flex-wrap sm:flex-col gap-2">
              {p.badges.map((b, i) => {
                const Icon = badgeIcons[i] || Check;
                return (
                  <div key={b.s} className="bg-white border border-af rounded-md px-2.5 py-2 flex items-center gap-2 w-28">
                    <div className="h-5 w-5 rounded-full bg-[#F5EFD8] flex items-center justify-center shrink-0">
                      <Icon className="h-2.5 w-2.5 text-terracotta" />
                    </div>
                    <div>
                      <div className="text-[9px] font-bold leading-none">{b.v}</div>
                      <div className="text-[8px] text-af-muted mt-0.5">{b.s}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Hero() {
  const { t } = useLang();
  const pointIcons = [Sparkles, ListChecks, Check];
  return (
    <section className="relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 py-16 md:py-20 grid lg:grid-cols-[1fr_1.15fr] gap-12 items-center">
        <div className="min-w-0">
          <h1 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
            {t.hero.t1}{" "}
            <span className="text-terracotta italic">{t.hero.accent}</span>
          </h1>
          <p className="mt-6 text-base md:text-lg text-af-secondary leading-relaxed max-w-xl">
            {t.hero.p1}
          </p>
          <p className="mt-3 text-base md:text-lg text-af-secondary leading-relaxed max-w-xl">
            {t.hero.p2}
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <Link to="/auth/signup" data-testid="hero-cta-signup">
              <Button size="lg" className="bg-terracotta hover:bg-[#9C7124] text-white rounded-md h-12 px-7 font-semibold">
                {t.hero.cta}
              </Button>
            </Link>
          </div>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4 text-xs text-af-secondary">
            {t.hero.points.map((p, i) => {
              const Icon = pointIcons[i];
              return (
                <div key={p.b} className="flex items-start gap-2">
                  <Icon className="h-4 w-4 text-terracotta shrink-0 mt-0.5" />
                  <span><strong className="text-af font-semibold">{p.b}</strong> {p.s}</span>
                </div>
              );
            })}
          </div>
        </div>

        <div className="relative min-w-0">
          <DashboardPreview />
        </div>
      </div>
    </section>
  );
}

/* ---------- Trust section removed (user request) ---------- */

/* ---------- How it works ---------- */
export function How() {
  const { t } = useLang();
  const stepIcons = [Calendar, Mic, FileText];
  const steps = t.how.steps.map((s, i) => ({ ...s, n: i + 1, icon: stepIcons[i] }));
  return (
    <section id="how" className="max-w-7xl mx-auto px-6 py-20 relative">
      <div className="text-center mb-3">
        <PatternDivider />
      </div>
      <h2 className="font-display font-extrabold text-3xl md:text-4xl text-center mb-14">
        {t.how.title}
      </h2>
      <div className="grid md:grid-cols-3 gap-6 md:gap-10 max-w-5xl mx-auto relative">
        {steps.map((s, i) => (
          <div key={s.t} className="text-center relative">
            <div className="relative inline-flex flex-col items-center">
              <div className="absolute -top-3 -right-2 h-7 w-7 rounded-full bg-terracotta text-white font-bold text-xs flex items-center justify-center z-10 shadow-md">
                {s.n}
              </div>
              <div className="h-20 w-20 rounded-full bg-white border-2 border-af flex items-center justify-center mb-5 shadow-sm">
                <s.icon className="h-8 w-8 text-terracotta" />
              </div>
            </div>
            <h3 className="font-display font-bold text-lg mb-2">{s.t}</h3>
            <p className="text-sm text-af-secondary leading-relaxed max-w-xs mx-auto">{s.d}</p>
            {i < steps.length - 1 && (
              <div className="hidden md:block absolute top-10 -right-5 text-terracotta/40 text-2xl">→</div>
            )}
          </div>
        ))}
      </div>
      <div className="text-center mt-12">
        <Link to="/auth/signup">
          <Button size="lg" className="bg-terracotta hover:bg-[#9C7124] text-white rounded-md h-12 px-8 font-semibold">
            {t.how.cta}
          </Button>
        </Link>
      </div>
    </section>
  );
}

/* ---------- Features ---------- */
export function Features() {
  const { t } = useLang();
  const icons = [Mic, FileText, ListChecks, Check, Download, Search];
  const features = t.features.items.map((f, i) => ({ ...f, icon: icons[i] }));
  return (
    <section id="features" className="bg-[#F5EFD8] border-y border-af">
      <div className="max-w-7xl mx-auto px-6 py-20">
        <h2 className="font-display font-extrabold text-3xl md:text-4xl text-center mb-14">
          {t.features.title}
        </h2>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {features.map((f) => (
            <div key={f.t} className="bg-white border border-af rounded-md p-6 hover:shadow-md hover:-translate-y-0.5 transition-all">
              <div className="h-10 w-10 rounded-md bg-[#F5EFD8] border border-af flex items-center justify-center mb-4">
                <f.icon className="h-5 w-5 text-terracotta" />
              </div>
              <h3 className="font-display font-bold text-base mb-1.5">{f.t}</h3>
              <p className="text-sm text-af-secondary leading-relaxed">{f.d}</p>
            </div>
          ))}
        </div>
        <div className="mt-14 flex justify-center items-center gap-6 flex-wrap">
          <div className="text-sm font-semibold text-af-secondary">{t.features.langLabel}</div>
          <div className="flex gap-3">
            <div className="bg-white border border-af rounded-md px-4 py-2 flex items-center gap-2 font-semibold text-sm">
              <span>🇫🇷</span> {t.features.french}
            </div>
            <div className="bg-white border border-af rounded-md px-4 py-2 flex items-center gap-2 font-semibold text-sm">
              <span>🇬🇧</span> {t.features.english}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------- Sectors ---------- */
export function Sectors() {
  const { t } = useLang();
  const icons = [Landmark, Building2, HeartHandshake, Briefcase, Radio];
  const sectors = t.sectors.items.map((s, i) => ({ ...s, icon: icons[i] }));
  return (
    <section id="sectors" className="max-w-7xl mx-auto px-6 py-20 relative">
      <div className="text-center mb-3">
        <PatternDivider />
      </div>
      <h2 className="font-display font-extrabold text-3xl md:text-4xl text-center mb-14">
        {t.sectors.title}
      </h2>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-5">
        {sectors.map((s) => (
          <div key={s.t} className="text-center group">
            <div className="h-20 w-20 mx-auto rounded-full bg-[#F5EFD8] border border-af flex items-center justify-center mb-4 group-hover:scale-105 group-hover:bg-[#EFE5C8] transition-all shadow-sm">
              <s.icon className="h-8 w-8 text-terracotta" />
            </div>
            <h3 className="font-display font-bold text-sm md:text-base mb-1.5">{s.t}</h3>
            <p className="text-xs md:text-sm text-af-secondary leading-relaxed">{s.d}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ---------- Pricing ---------- */
export function Pricing() {
  const { t } = useLang();
  const p = t.pricing;
  return (
    <section id="pricing" className="bg-[#F5EFD8] border-y border-af">
      <div className="max-w-5xl mx-auto px-6 py-20">
        <h2 className="font-display font-extrabold text-3xl md:text-4xl text-center mb-14">
          {p.title}
        </h2>
        <div className="grid md:grid-cols-2 gap-5">
          {/* Starter */}
          <div className="bg-white border border-af rounded-md p-8 flex flex-col">
            <div className="text-xs uppercase tracking-widest text-af-muted font-semibold mb-2">{p.starter.tier}</div>
            <div className="font-display font-extrabold text-3xl md:text-4xl mb-1">{p.starter.name}</div>
            <div className="text-sm text-af-muted mb-8">{p.starter.sub}</div>
            <ul className="space-y-2.5 mb-8 flex-1">
              {p.starter.features.map((f) => (
                <li key={f} className="flex items-start gap-2.5 text-sm text-af-secondary">
                  <Check className="h-4 w-4 text-terracotta shrink-0 mt-0.5" /> {f}
                </li>
              ))}
            </ul>
            <Link to="/auth/signup">
              <Button className="w-full bg-terracotta hover:bg-[#9C7124] text-white rounded-md h-11 font-semibold">
                {p.starter.cta}
              </Button>
            </Link>
            <p className="text-xs text-af-muted text-center mt-3">{p.starter.note}</p>
          </div>

          {/* Business */}
          <div className="bg-[#2B1F12] text-white border border-[#2B1F12] rounded-md p-8 flex flex-col relative overflow-hidden">
            <div className="absolute top-0 right-0 bg-terracotta text-white text-[10px] uppercase tracking-widest font-bold px-3 py-1 rounded-bl-md">
              {p.business.badge}
            </div>
            <div className="text-xs uppercase tracking-widest text-white/60 font-semibold mb-2">{p.business.tier}</div>
            <div className="font-display font-extrabold text-3xl md:text-4xl mb-1">
              35 000 <span className="text-xl text-white/60">FCFA</span>
              <span className="text-sm font-normal text-white/60"> {p.business.period}</span>
            </div>
            <div className="text-sm text-white/60 mb-8">{p.business.sub}</div>
            <ul className="space-y-2.5 mb-8 flex-1">
              {p.business.features.map((f) => (
                <li key={f} className="flex items-start gap-2.5 text-sm text-white/85">
                  <Check className="h-4 w-4 text-[#D4A04E] shrink-0 mt-0.5" /> {f}
                </li>
              ))}
            </ul>
            <Link to="/auth/signup">
              <Button className="w-full bg-terracotta hover:bg-[#9C7124] text-white rounded-md h-11 font-semibold">
                {p.business.cta}
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------- FAQ ---------- */
export function FAQ() {
  const { t } = useLang();
  const faqs = t.faq.items;
  return (
    <section className="bg-[#F5EFD8] border-y border-af">
      <div className="max-w-5xl mx-auto px-6 py-20">
        <h2 className="font-display font-extrabold text-3xl md:text-4xl text-center mb-14">
          {t.faq.title}
        </h2>
        <Accordion type="single" collapsible className="grid md:grid-cols-2 gap-x-8">
          {faqs.map((f, i) => (
            <AccordionItem key={f.q} value={`item-${i}`} className="border-b border-af">
              <AccordionTrigger className="text-left font-medium text-af hover:text-terracotta hover:no-underline py-4">
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-sm text-af-secondary leading-relaxed pb-4">
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}

/* ---------- Footer ---------- */
export function Footer() {
  const { t } = useLang();
  return (
    <footer className="bg-[#2B1F12] text-white">
      <div className="max-w-7xl mx-auto px-6 py-14 grid md:grid-cols-2 lg:grid-cols-4 gap-10">
        <div className="lg:col-span-1">
          <Link to="/" data-testid="footer-logo-link" className="inline-block">
            <Logo size={60} className="mb-4" />
            <div className="font-display font-extrabold text-2xl mb-2">AfriMeet</div>
          </Link>
          <p className="text-sm text-white/60 leading-relaxed">
            {t.footer.tagline}
          </p>
        </div>
        <div>
          <div className="font-display font-bold mb-4">{t.footer.product}</div>
          <ul className="space-y-2 text-sm text-white/70">
            <li><Link to="/features" data-testid="footer-features-link" className="hover:text-white">{t.nav.features}</Link></li>
            <li><Link to="/pricing" data-testid="footer-pricing-link" className="hover:text-white">{t.nav.pricing}</Link></li>
          </ul>
        </div>
        <div>
          <div className="font-display font-bold mb-4">{t.footer.company}</div>
          <ul className="space-y-2 text-sm text-white/70">
            <li><Link to="/about" data-testid="footer-about-link" className="hover:text-white">{t.nav.about}</Link></li>
            <li><Link to="/contact" data-testid="footer-contact-link" className="hover:text-white">{t.nav.contact}</Link></li>
          </ul>
        </div>
        <div>
          <div className="font-display font-bold mb-4">{t.footer.langs}</div>
          <div className="flex gap-3 mb-6">
            <span className="text-sm text-white/80">🇫🇷 {t.features.french}</span>
            <span className="text-sm text-white/80">🇬🇧 {t.features.english}</span>
          </div>
          <div className="flex gap-2">
            {[["linkedin", Linkedin], ["facebook", Facebook], ["twitter", Twitter]].map(([name, I]) => (
              <a key={name} href="#" className="h-9 w-9 rounded-full border border-white/20 flex items-center justify-center hover:bg-white/10 transition-colors">
                <I className="h-4 w-4" />
              </a>
            ))}
          </div>
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-5 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-white/50">
          <div>{t.footer.rights}</div>
          <div className="flex gap-6">
            <Link to="/legal/notice" data-testid="footer-legal-notice" className="hover:text-white">{t.footer.legal[0]}</Link>
            <Link to="/legal/privacy" data-testid="footer-legal-privacy" className="hover:text-white">{t.footer.legal[1]}</Link>
            <Link to="/legal/terms" data-testid="footer-legal-terms" className="hover:text-white">{t.footer.legal[2]}</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}

/* ---------- Product Showcase ---------- */
export function ProductShowcase() {
  const { t } = useLang();
  const ann = t.showcase.annotations;
  const c = t.showcase.card;
  return (
    <section className="relative overflow-hidden bg-af">
      <AdinkraDots className="absolute top-10 left-0 w-64 h-64 opacity-40" color="#B8862D" />
      <AdinkraDots className="absolute bottom-10 right-0 w-64 h-64 opacity-40" color="#B8862D" />
      <div className="max-w-7xl mx-auto px-6 py-20 relative">
        <div className="grid lg:grid-cols-[200px_1fr_200px] gap-8 items-center">
          {/* Left annotations */}
          <div className="hidden lg:flex flex-col gap-20">
            <Annotation title={ann[0].t} desc={ann[0].d} align="left" />
            <Annotation title={ann[1].t} desc={ann[1].d} align="left" />
          </div>

          {/* Center showcase card */}
          <div className="bg-af-card border border-af rounded-lg p-6 md:p-8 shadow-[0_12px_40px_-15px_rgba(184,134,45,0.25)] relative min-w-0">
            <div className="flex items-center gap-2.5 mb-5">
              <Logo size={28} />
              <h3 className="font-display font-bold text-base md:text-lg">{c.title}</h3>
            </div>

            {/* Tabs */}
            <div className="flex flex-wrap gap-x-5 gap-y-1 border-b border-af mb-5 text-sm">
              {c.tabs.map((tab, i) => (
                <button
                  key={tab}
                  className={`pb-2.5 -mb-px transition-colors ${
                    i === 0
                      ? "text-terracotta border-b-2 border-terracotta font-semibold"
                      : "text-af-secondary hover:text-af"
                  }`}
                >
                  {tab}
                </button>
              ))}
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {/* Left column: Summary + waveform */}
              <div>
                <div className="text-xs font-bold text-af mb-2">{c.execTitle}</div>
                <p className="text-xs md:text-sm text-af-secondary leading-relaxed mb-4">
                  {c.execText}
                </p>
                {/* Audio waveform mockup */}
                <div className="bg-af-subtle border border-af rounded-md p-3">
                  <div className="flex items-center gap-1 sm:gap-2 h-8 mb-2 overflow-hidden">
                    {Array.from({ length: 42 }).map((_, i) => {
                      const heights = [
                        4, 8, 14, 6, 18, 10, 22, 12, 6, 16, 24, 8, 14, 20, 10,
                        6, 18, 14, 22, 10, 16, 8, 12, 20, 6, 14, 18, 10, 22, 16,
                        8, 14, 6, 18, 10, 12, 16, 22, 8, 14, 6, 18,
                      ];
                      return (
                        <span
                          key={i}
                          style={{ height: heights[i] }}
                          className={`w-0.5 rounded-full ${i < 12 ? "bg-terracotta" : "bg-af-border"}`}
                        />
                      );
                    })}
                  </div>
                  <div className="flex items-center justify-between text-[10px] text-af-muted">
                    <div className="flex items-center gap-1.5">
                      <button className="h-5 w-5 rounded-full bg-terracotta flex items-center justify-center">
                        <Play className="h-2.5 w-2.5 text-white fill-white" />
                      </button>
                      <span className="font-mono">00:00 / 46:31</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-mono">1x</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right column: Decisions + actions */}
              <div className="space-y-4">
                <div>
                  <div className="text-xs font-bold text-af mb-2">{c.decisionsTitle}</div>
                  <ul className="space-y-1.5">
                    {c.decisions.map((d) => (
                      <li key={d} className="flex items-start gap-2 text-xs md:text-sm text-af-secondary">
                        <span className="h-4 w-4 rounded-full bg-[#F5EFD8] border border-terracotta/50 flex items-center justify-center shrink-0 mt-0.5">
                          <Check className="h-2.5 w-2.5 text-terracotta" />
                        </span>
                        {d}
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <div className="text-xs font-bold text-af mb-2">{c.actionsTitle}</div>
                  <div className="space-y-2">
                    {c.actions.map((a, i) => (
                      <div key={a.n} className="flex items-center gap-2 text-[11px] py-1.5 border-b border-af last:border-0">
                        <div
                          className="h-6 w-6 rounded-full shrink-0 bg-cover bg-center"
                          style={{
                            backgroundColor: "#D4A04E",
                            backgroundImage: `url(https://i.pravatar.cc/40?img=${10 + i})`,
                          }}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-af truncate">{a.n}</div>
                          <div className="text-af-muted truncate">{a.t}</div>
                        </div>
                        <div className="text-af-muted whitespace-nowrap text-[10px]">{a.d} →</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right annotations */}
          <div className="hidden lg:flex flex-col gap-20">
            <Annotation title={ann[2].t} desc={ann[2].d} align="right" />
            <Annotation title={ann[3].t} desc={ann[3].d} align="right" />
          </div>
        </div>
      </div>
    </section>
  );
}

function Annotation({ title, desc, align }) {
  return (
    <div className={`relative ${align === "left" ? "text-right" : "text-left"}`}>
      <div className="font-display font-bold text-sm text-af mb-1">{title}</div>
      <div className="text-xs text-af-secondary leading-relaxed">{desc}</div>
      <svg
        viewBox="0 0 100 30"
        className={`absolute top-1/2 ${align === "left" ? "left-full -ml-2" : "right-full -mr-2"} w-16 h-8 text-terracotta/50 -translate-y-1/2`}
        preserveAspectRatio="none"
      >
        {align === "left" ? (
          <path d="M0 15 Q50 5 95 15 M85 8 L95 15 L85 22" stroke="currentColor" fill="none" strokeWidth="1.5" strokeLinecap="round" strokeDasharray="2 3" />
        ) : (
          <path d="M100 15 Q50 5 5 15 M15 8 L5 15 L15 22" stroke="currentColor" fill="none" strokeWidth="1.5" strokeLinecap="round" strokeDasharray="2 3" />
        )}
      </svg>
    </div>
  );
}

/* ---------- Main ---------- */
export default function LandingPage() {
  return (
    <div className="min-h-screen bg-af">
      <Header />
      <Hero />
      <How />
      <Features />
      <ProductShowcase />
      <Sectors />
      <Pricing />
      <FAQ />
      <Footer />
    </div>
  );
}

